import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Breadcrumbs,
  Card,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Chip,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import LoadingUI from "../../comman/LoadingUI";
import NoDataFound from "../../comman/NoDataFound";
import SnackBarUI from "../../comman/SnackBarUI";
import Appbar from "../../components/Appbar";
import Body from "../../layout/Body";
import { useStyles } from "../../theme/theme";
import { getAllUserOfferAttemptsAction } from "../../redux/toolkit/userOfferAttempt/getAllUserOfferAttempts";
import { deleteUserOfferAttemptAction, resetDeleteUserOfferAttemptState } from "../../redux/toolkit/userOfferAttempt/deleteUserOfferAttempt";
import { getAllOffersAction } from "../../redux/toolkit/offer/getAllOffers";
import { getAllPartnerActionNoLimit } from "../../redux/toolkit/partner/getAllPartnerNoLimit";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import DailogUi from "../../comman/DailogUi";
import ViewDetailsModal from "./ViewDetailsModal";

const breadcrumb = (
  <Stack>
    <Breadcrumbs
      separator={
        <NavigateNextIcon sx={{ color: "#5E6C8A" }} fontSize="small" />
      }
      aria-label="breadcrumb"
    >
      <Link
        underline="hover"
        sx={{ color: "#5E6C8A" }}
        color="inherit"
        href="/"
      >
        Home
      </Link>
      <Link
        underline="hover"
        sx={{ color: "#5E6C8A" }}
        color="inherit"
        href="/user-offer-attempts"
      >
        User Offer Attempts
      </Link>
    </Breadcrumbs>
    <Typography sx={{ color: "#5E6C8A" }} variant="h4">
      User Offer Attempts
    </Typography>
  </Stack>
);

const UserOfferAttempts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const classes = useStyles();
  const dispatch = useDispatch();

  // Get initial values from URL params
  const initialPage = parseInt(searchParams.get("page")) || 0;
  const initialPageSize = parseInt(searchParams.get("pageSize")) || 25;
  const initialSearch = searchParams.get("search") || "";
  const initialStatus = searchParams.get("status") || "";
  const initialOfferId = searchParams.get("offer_id") || "";
  const initialPartnerId = searchParams.get("partner_id") || "";

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedAttemptId, setSelectedAttemptId] = useState("");
  const [viewDetailsModal, setViewDetailsModal] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [snack, setSnack] = useState(false);
  
  const [pageState, setPageState] = useState({
    page: initialPage,
    pageSize: initialPageSize,
  });
  const [search, setSearch] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [offerIdFilter, setOfferIdFilter] = useState(initialOfferId);
  const [partnerIdFilter, setPartnerIdFilter] = useState(initialPartnerId);

  const { data, message, totalPages, totalDocs, loading } = useSelector(
    (state) => state.getAllUserOfferAttempts
  );

  const { data: offersData } = useSelector((state) => state.getAllOffers);
  const { data: partnersData } = useSelector(
    (state) => state.allPartnerWithoutLimit
  );

  const deleteAttemptSelector = useSelector((state) => state.deleteUserOfferAttempt);

  // Update URL params when filters change
  useEffect(() => {
    const params = {
      page: pageState.page.toString(),
      pageSize: pageState.pageSize.toString(),
    };
    if (search) params.search = search;
    if (statusFilter !== "") params.status = statusFilter;
    if (offerIdFilter) params.offer_id = offerIdFilter;
    if (partnerIdFilter) params.partner_id = partnerIdFilter;
    setSearchParams(params);
  }, [pageState.page, pageState.pageSize, search, statusFilter, offerIdFilter, partnerIdFilter, setSearchParams]);

  // Fetch data
  useEffect(() => {
    dispatch(
      getAllUserOfferAttemptsAction({
        page: pageState.page,
        pageSize: pageState.pageSize,
        search: search,
        status: statusFilter,
        offer_id: offerIdFilter,
        partner_id: partnerIdFilter,
      })
    );
  }, [pageState.page, pageState.pageSize, search, statusFilter, offerIdFilter, partnerIdFilter, dispatch]);

  // Fetch offers and partners for filters
  useEffect(() => {
    dispatch(getAllOffersAction({ page: 0, pageSize: 1000, search: "" }));
    dispatch(getAllPartnerActionNoLimit());
    // Reset delete state on component mount
    dispatch(resetDeleteUserOfferAttemptState());
  }, [dispatch]);

  // Handle snackbar for delete
  useEffect(() => {
    if (deleteAttemptSelector.status === 200) {
      setSnack(true);
      setDeleteDialog(false);
      dispatch(
        getAllUserOfferAttemptsAction({
          page: pageState.page,
          pageSize: pageState.pageSize,
          search: search,
          status: statusFilter,
          offer_id: offerIdFilter,
          partner_id: partnerIdFilter,
        })
      );
    }
  }, [deleteAttemptSelector.status]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPageState((old) => ({ ...old, page: 0 }));
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPageState((old) => ({ ...old, page: 0 }));
  };

  const handleOfferChange = (event) => {
    setOfferIdFilter(event.target.value);
    setPageState((old) => ({ ...old, page: 0 }));
  };

  const handlePartnerChange = (event) => {
    setPartnerIdFilter(event.target.value);
    setPageState((old) => ({ ...old, page: 0 }));
  };

  const handleDeleteClick = (id) => {
    setSelectedAttemptId(id);
    setDeleteDialog(true);
    // Reset delete state when opening dialog
    dispatch(resetDeleteUserOfferAttemptState());
  };

  const handleDeleteConfirm = () => {
    dispatch(deleteUserOfferAttemptAction({ attemptId: selectedAttemptId }));
  };

  const handleDialogClose = () => {
    setDeleteDialog(false);
    dispatch(resetDeleteUserOfferAttemptState());
  };

  const handleViewDetails = (attempt) => {
    setSelectedAttempt(attempt);
    setViewDetailsModal(true);
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 0:
        return <Chip label="Pending" color="warning" size="small" />;
      case 1:
        return <Chip label="Completed" color="success" size="small" />;
      case 2:
        return <Chip label="Failed" color="error" size="small" />;
      case 3:
        return <Chip label="Cancelled" color="default" size="small" />;
      default:
        return <Chip label="Unknown" size="small" />;
    }
  };

  const columns = [
    {
      field: "s_no",
      headerName: "S. No.",
      filterable: false,
      flex: 0.5,
      minWidth: 80,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        const { page, pageSize } = pageState;
        const serialNumber =
          page * pageSize + params?.api?.getRowIndex(params.row._id) + 1;
        return serialNumber;
      },
    },
    {
      field: "_id",
      headerName: "Clickid",
      flex: 1,
      minWidth: 200,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        return <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{params?.row?._id || "N/A"}</Typography>;
      },
    },
    {
      field: "user_id",
      headerName: "User",
      flex: 1,
      minWidth: 180,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="body2">{params?.row?.user_id?.email || "N/A"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {params?.row?.user_id?.name || ""}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "offer_id",
      headerName: "Offer",
      flex: 1,
      minWidth: 150,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        return <Typography variant="body2">{params?.row?.offer_id?.name || "N/A"}</Typography>;
      },
    },
    {
      field: "partner_id",
      headerName: "Partner",
      flex: 1,
      minWidth: 120,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        return <Typography variant="body2">{params?.row?.partner_id?.name || "N/A"}</Typography>;
      },
    },
    {
      field: "trans_id",
      headerName: "Transaction ID",
      flex: 1,
      minWidth: 150,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "payout_received",
      headerName: "Payout",
      flex: 0.8,
      minWidth: 100,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        return <Typography>{params?.row?.payout_received || 0}</Typography>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 120,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        return getStatusChip(params?.row?.status);
      },
    },
    {
      field: "clicked_on",
      headerName: "Started At",
      flex: 1,
      minWidth: 180,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        return moment(params?.row?.clicked_on).format("DD-MM-YYYY hh:mm A");
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 120,
      headerClassName: "super-app-theme--header",
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={() => handleViewDetails(params?.row)}
              color="primary"
              size="small"
            >
              <VisibilityIcon />
            </IconButton>
            <IconButton
              onClick={() => handleDeleteClick(params?.row?._id)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>Real Opinion Admin | User Offer Attempts</title>
      </Helmet>
      <Appbar breadcrumb={breadcrumb} />
      <Body>
        <Stack
          direction="row"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignContent: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 2 }}>
            <TextField
              label="Search by Trans ID / User ID"
              variant="outlined"
              size="small"
              value={search}
              onChange={handleSearchChange}
              sx={{ minWidth: 250 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={handleStatusChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="0">Pending</MenuItem>
                <MenuItem value="1">Completed</MenuItem>
                <MenuItem value="2">Failed</MenuItem>
                <MenuItem value="3">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="offer-filter-label">Filter by Offer</InputLabel>
              <Select
                labelId="offer-filter-label"
                value={offerIdFilter}
                label="Filter by Offer"
                onChange={handleOfferChange}
              >
                <MenuItem value="">All Offers</MenuItem>
                {offersData?.map((offer) => (
                  <MenuItem key={offer._id} value={offer._id}>
                    {offer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="partner-filter-label">Filter by Partner</InputLabel>
              <Select
                labelId="partner-filter-label"
                value={partnerIdFilter}
                label="Filter by Partner"
                onChange={handlePartnerChange}
              >
                <MenuItem value="">All Partners</MenuItem>
                {partnersData?.map((partner) => (
                  <MenuItem key={partner._id} value={partner._id}>
                    {partner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <Card sx={{ my: 2 }}>
          <Stack direction={"row"} alignItems="center" sx={{ ml: 1 }}>
            <Typography sx={{ my: 1 }} variant="h6">
              User Offer Attempts ({totalDocs || 0})
            </Typography>
          </Stack>

          <Paper
            sx={{
              bgcolor: "#fff",
              "& .super-app-theme--header": {
                bgcolor: "#071D45",
                color: "#fff",
              },
            }}
            elevation={0}
          >
            <DataGrid
              className={classes.root}
              getRowHeight={() => "auto"}
              sx={{ border: 0 }}
              rows={data || []}
              autoHeight
              columns={columns}
              getRowId={(row) => row._id}
              disableSelectionOnClick
              experimentalFeatures={{ newEditingApi: true }}
              pagination
              rowsPerPageOptions={[25, 50, 100]}
              disableColumnSelector
              loading={loading}
              components={{
                NoRowsOverlay: () => <NoDataFound />,
                LoadingOverlay: () => <LoadingUI />,
              }}
              rowCount={totalDocs}
              paginationMode="server"
              page={pageState.page}
              pageSize={pageState.pageSize}
              onPageChange={(newPage) => {
                setPageState((old) => ({ ...old, page: newPage }));
              }}
              onPageSizeChange={(newPageSize) =>
                setPageState((old) => ({ ...old, pageSize: newPageSize }))
              }
            />
          </Paper>
        </Card>

        {deleteDialog && (
          <DailogUi
            open={deleteDialog}
            setOpen={handleDialogClose}
            title="Delete User Offer Attempt"
            description="Are you sure you want to delete this user offer attempt? This action cannot be undone."
            onConfirm={handleDeleteConfirm}
            loading={deleteAttemptSelector.loading}
          />
        )}

        {viewDetailsModal && selectedAttempt && (
          <ViewDetailsModal
            open={viewDetailsModal}
            setOpen={setViewDetailsModal}
            attempt={selectedAttempt}
          />
        )}

        {snack && (
          <SnackBarUI
            open={snack}
            setOpen={setSnack}
            message={deleteAttemptSelector.message || ""}
            type={deleteAttemptSelector.status === 200 ? "success" : "error"}
          />
        )}
      </Body>
    </>
  );
};

export default UserOfferAttempts;
